<template>
  <div class="test">
    <PartitionsTable />
    <PriceQuoteCheckbox />

    <div class="step__block-field">
      <h3>Partition price:</h3>
      <PriceValue :price="estimateSubtotal" class="step__block-field-price" />
    </div>

    <div class="discount-block estimate-text">
      <DiscountPercentField :hideInput="true" />
      <DiscountSumField :hideInput="true" />
    </div>

    <div class="step__block-field top">
      <h3>Discounted partition price:</h3>
      <PriceValue :price="estimatePrice" class="step__block-field-price" />
    </div>

    <Subtotal />
    <TaxBlock />
    <PageTotal />

    <p class="textarea-label">Additional notice for customer</p>
    <textarea
      v-model="estimateStore.estimate.customerNotice"
      class="textarea"
      placeholder="Type here"
    ></textarea>

    <ControlButtons />
  </div>
</template>

<script setup lang="ts">
import { PriceValue } from "@/shared";
import { DiscountPercentField, DiscountSumField } from "@/modules/estimate";
import {
  ControlButtons,
  PriceQuoteCheckbox,
  Subtotal,
  PageTotal,
  TaxBlock,
  PartitionsTable,
} from "@/modules/estimate-review";

import { computed, watch } from "vue";
import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { Pricing } from "@/app/domain";
import { PricingService } from "@/app/services/pricing.service";
import { useExtraStore } from "@/stores/new/extra.store";
import { useOverheadStore } from "@/stores/new/overhead.store";

defineExpose({ validatePage });

const estimateStore = useNewEstimateStore();
const pricingService = new PricingService();

const overheadStore = useOverheadStore();
const extraStore = useExtraStore();

const estimate = computed(() => estimateStore.estimate);
const estimateSubtotal = computed(() => estimate.value.subtotal);
const estimatePrice = computed(() => estimate.value.estimatePrice);
const dealType = computed(() => estimateStore.estimate.dealTypeCode);

function validatePage() {
  return null;
}

watch(
  () => [estimateStore.estimate.subtotal, estimateStore.estimate.discountSum],
  () => {
    const discountedPrice = pricingService.calculateEstimatePrice();
    const formattedDiscountedPrice = new Pricing(
      discountedPrice.price,
      discountedPrice.priceB2C,
      discountedPrice.priceB2B,
    );
    estimateStore.estimate.estimatePrice = formattedDiscountedPrice;
  },
  { deep: true, immediate: true },
);
</script>

<style scoped>
.test {
  -webkit-tap-highlight-color: transparent;
  tap-highlight-color: transparent;
  outline: none;
  user-select: none;
}
.discount-block {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
}

.estimate-text {
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  line-height: 100%;
  color: #052141;
}

.step__block-field {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid #d2d8e3;
}

.step__block-field.top {
  padding-bottom: 0;
  padding-top: 24px;
  margin-top: 24px;

  border-bottom: none;
  border-top: 1px solid #d2d8e3;
}

.step__block-field h3 {
  color: #404d63;
  font-family: Gilroy;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
}

.step__block-field-price {
  color: #404d63;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
}

.textarea-label {
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  line-height: 100%;
  color: #052141;

  margin-top: 32px;
  margin-bottom: 16px;
}

.textarea {
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  line-height: 100%;
  color: #052141;

  width: 100%;
  min-height: 150px;
  padding-block: 20px;
  padding-inline: 24px;
  background: #fff;
  border-radius: 10px;
}

.textarea::placeholder {
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  line-height: 100%;
  color: rgba(5, 33, 65, 0.5);
}
</style>
